/**
 * External Exposure Center tab.
 *
 * Two free, no-API-key public sources back this view:
 *   1. Have I Been Pwned — public Breaches directory (org-level breach
 *      metadata for a domain).
 *   2. Hudson Rock — Cavalier OSINT (infostealer infections referencing the
 *      domain or email).
 *
 * The tab has two sections:
 *   • Autonomous Exposure Feed — correlates OSINT findings from the background agent.
 *   • Credential & Breach Exposure — checks watched domains through public sources.
 *   • Ad-hoc Lookup — search-by-email or search-by-domain, on demand.
 *
 * All numeric fields are null-safe (em-dash fallback). Provider failures
 * are surfaced as inline warnings without breaking the rest of the tab.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
// Note: this tab's headline buttons ("Re-check", "Check lookup") hit READ
// endpoints (/darkweb/scan, /darkweb/lookup) so they aren't gated against
// the darkweb tab write matrix. Domain add/remove stays admin-only via
// `isAdmin` below. The actual darkweb write surface (ack finding,
// promote-to-incident) lives in `NeoBriefingCard` which does its own
// canWrite('darkweb', activeCustomerId) check.
import { NeoBriefingCard } from './NeoBriefingCard';
import { fmtTs } from '../lib/globalTz';

// ── Types mirroring the server response shapes ──────────────────────────
interface ProviderEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: string;
  fetchedAt: string;
}

interface HibpBreach {
  name: string;
  title: string;
  domain: string;
  breachDate: string;
  pwnCount: number;
  description: string;
  dataClasses: string[];
  isVerified: boolean;
  isFabricated: boolean;
  isSensitive: boolean;
  isMalware: boolean;
  logoPath?: string;
}
interface HibpDomainResult {
  breachCount: number;
  totalAccountsPwned: number;
  mostRecentBreachDate?: string;
  topDataClasses: Array<{ name: string; count: number }>;
  breaches: HibpBreach[];
}

interface UrlHit { url: string; occurrence: number; type?: string }
interface PasswordStrength {
  totalPass: number;
  buckets: Array<{ label: string; qty: number; perc: number }>;
}
interface AntivirusCoverage {
  total: number;
  withAvPercent: number;
  withoutAvPercent: number;
  topProducts: Array<{ name: string; count: number }>;
}
interface HudsonRockDomainResult {
  total: number;
  totalStealers: number;
  employees: number;
  users: number;
  thirdParties: number;
  attackedDomain?: string;
  logoUrl?: string;
  lastEmployeeCompromised?: string;
  lastUserCompromised?: string;
  topEmployeeUrls: UrlHit[];
  topClientUrls: UrlHit[];
  stealerFamilies: Array<{ name: string; count: number }>;
  topApplications: Array<{ name: string; count: number }>;
  topThirdPartyDomains: Array<{ domain: string; occurrence: number }>;
  employeePasswords?: PasswordStrength;
  userPasswords?: PasswordStrength;
  antivirusCoverage?: AntivirusCoverage;
}

interface HudsonRockEmailResult {
  found: boolean;
  total: number;
  totalCorporateServices: number;
  totalUserServices: number;
  message?: string;
  infections: Array<{
    stealerFamily?: string;
    date?: string;
    computerName?: string;
    operatingSystem?: string;
    ip?: string;
    country?: string;
    malwarePath?: string;
  }>;
}

interface DomainScanResult {
  domain: string;
  hibp: ProviderEnvelope<HibpDomainResult>;
  hudsonRock: ProviderEnvelope<HudsonRockDomainResult>;
}

interface ScanResponse {
  customerId: string;
  customerName: string;
  monitoredDomains: string[];
  domains: DomainScanResult[];
  generatedAt: string;
  message?: string;
}

interface EmailLookupResponse {
  kind: 'email';
  value: string;
  hudsonRock: ProviderEnvelope<HudsonRockEmailResult>;
  generatedAt: string;
}
interface DomainLookupResponse {
  kind: 'domain';
  value: string;
  domains: DomainScanResult[];
  generatedAt: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────
function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return ' - ';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function fmtDate(s: string | null | undefined): string {
  if (!s) return ' - ';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}
function exposureSeverity(hibpCount: number, hrTotal: number): 'critical' | 'high' | 'moderate' | 'low' | 'clean' {
  if (hibpCount >= 5 || hrTotal >= 100) return 'critical';
  if (hibpCount >= 2 || hrTotal >= 25) return 'high';
  if (hibpCount >= 1 || hrTotal >= 5) return 'moderate';
  if (hrTotal > 0) return 'low';
  return 'clean';
}
const SEV_PILL: Record<ReturnType<typeof exposureSeverity>, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  moderate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  low: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  clean: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
};
const SEV_LABEL: Record<ReturnType<typeof exposureSeverity>, string> = {
  critical: 'CRITICAL',
  high: 'HIGH',
  moderate: 'MODERATE',
  low: 'LOW',
  clean: 'CLEAN',
};


function openPhishingDomainScanner(domain?: string) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('tab', 'dnstwist');
  if (domain) url.searchParams.set('dnstwistTarget', domain);
  window.location.assign(url.toString());
}

function exposureSummary(scan: ScanResponse | null, monitoredDomains: string[]) {
  const domains = scan?.domains ?? [];
  let breachCount = 0;
  let accountsPwned = 0;
  let infostealerMachines = 0;
  let elevatedDomains = 0;
  let providerWarnings = 0;

  for (const d of domains) {
    if (!d.hibp.ok) providerWarnings += 1;
    if (!d.hudsonRock.ok) providerWarnings += 1;
    const hibp = d.hibp.data;
    const hr = d.hudsonRock.data;
    breachCount += hibp?.breachCount ?? 0;
    accountsPwned += hibp?.totalAccountsPwned ?? 0;
    infostealerMachines += hr?.total ?? 0;
    const sev = exposureSeverity(hibp?.breachCount ?? 0, hr?.total ?? 0);
    if (sev === 'critical' || sev === 'high' || sev === 'moderate') elevatedDomains += 1;
  }

  return {
    watchedDomains: monitoredDomains.length || scan?.monitoredDomains?.length || domains.length,
    domainsChecked: domains.length,
    breachCount,
    accountsPwned,
    infostealerMachines,
    elevatedDomains,
    providerWarnings,
  };
}

function ExposureOverviewCards({ scan, monitoredDomains }: { scan: ScanResponse | null; monitoredDomains: string[] }) {
  const s = exposureSummary(scan, monitoredDomains);
  const cards = [
    { label: 'Watched domains', value: fmtNum(s.watchedDomains), hint: `${fmtNum(s.domainsChecked)} checked in latest run` },
    { label: 'Breach records', value: fmtNum(s.breachCount), hint: `${fmtNum(s.accountsPwned)} exposed accounts reported` },
    { label: 'Infostealer machines', value: fmtNum(s.infostealerMachines), hint: 'Hudson Rock free OSINT visibility' },
    { label: 'Elevated domains', value: fmtNum(s.elevatedDomains), hint: 'Moderate / high / critical exposure' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {cards.map((card) => (
        <div key={card.label} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">{card.label}</div>
          <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{card.value}</div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{card.hint}</div>
        </div>
      ))}
      {s.providerWarnings > 0 ? (
        <div className="sm:col-span-2 xl:col-span-4 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          {s.providerWarnings} provider call{s.providerWarnings === 1 ? '' : 's'} returned a warning in the latest check. Review the domain cards below for source-specific errors.
        </div>
      ) : null}
    </div>
  );
}

function SourceSplitCard() {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">How this differs from the full lookalike scanner</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-4xl">
            External Exposure Center keeps the curated, actionable feed across OSINT sources. The full lookalike scanner remains the deep investigation console for generating every permutation, reviewing A/MX/NS/WHOIS evidence, exporting all checked candidates, and running AI confidence analysis.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openPhishingDomainScanner()}
          className="px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium"
        >
          Open Phishing Domain Scanner
        </button>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-3">
          <div className="font-semibold text-slate-800 dark:text-slate-100">Curated feed</div>
          <div className="text-slate-500 dark:text-slate-400 mt-1">Shows live/risky findings promoted by the autonomous sweep.</div>
        </div>
        <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-3">
          <div className="font-semibold text-slate-800 dark:text-slate-100">Full investigation</div>
          <div className="text-slate-500 dark:text-slate-400 mt-1">Use the full lookalike scanner for complete permutation inventory and registered-only/all-results exports.</div>
        </div>
        <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-3">
          <div className="font-semibold text-slate-800 dark:text-slate-100">Response workflow</div>
          <div className="text-slate-500 dark:text-slate-400 mt-1">Promote high-confidence exposure findings to Sentinel or acknowledge false positives from the feed.</div>
        </div>
      </div>
    </div>
  );
}

// ── Tab component ───────────────────────────────────────────────────────
export function DarkWebTab() {
  const { activeCustomerId, activeCustomer, refresh: refreshCustomers } = useCustomers();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'developer';

  const [scan, setScan] = useState<ScanResponse | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const [newDomain, setNewDomain] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainSaveError, setDomainSaveError] = useState<string | null>(null);

  // Ad-hoc lookup state
  const [lookupKind, setLookupKind] = useState<'email' | 'domain'>('email');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<EmailLookupResponse | DomainLookupResponse | null>(null);

  const loadScan = useCallback(async () => {
    if (!activeCustomerId) return;
    setScanLoading(true);
    setScanError(null);
    try {
      const data = await apiFetch<ScanResponse>(
        `/darkweb/scan?customerId=${encodeURIComponent(activeCustomerId)}`,
      );
      setScan(data);
    } catch (e) {
      setScanError(e instanceof Error ? e.message : 'Failed to run dark-web scan');
    } finally {
      setScanLoading(false);
    }
  }, [activeCustomerId]);

  useEffect(() => { loadScan(); }, [loadScan]);

  const monitoredDomains = useMemo(
    () => activeCustomer?.monitoredDomains ?? scan?.monitoredDomains ?? [],
    [activeCustomer?.monitoredDomains, scan?.monitoredDomains],
  );

  async function addDomain(e: React.FormEvent) {
    e.preventDefault();
    const value = newDomain.trim().toLowerCase();
    if (!value) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) {
      setDomainSaveError('Enter a valid domain (e.g. acme.com)');
      return;
    }
    if (monitoredDomains.includes(value)) {
      setDomainSaveError('Domain is already monitored');
      return;
    }
    setDomainSaving(true);
    setDomainSaveError(null);
    try {
      const next = Array.from(new Set([...monitoredDomains, value]));
      await apiFetch(`/darkweb/monitored-domains`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId, domains: next }),
      });
      setNewDomain('');
      await refreshCustomers();
      await loadScan();
    } catch (err) {
      setDomainSaveError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setDomainSaving(false);
    }
  }

  async function removeDomain(domain: string) {
    if (!isAdmin) return;
    if (!window.confirm(`Stop monitoring ${domain}?`)) return;
    setDomainSaveError(null);
    try {
      const next = monitoredDomains.filter((d) => d !== domain);
      await apiFetch(`/darkweb/monitored-domains`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId, domains: next }),
      });
      await refreshCustomers();
      await loadScan();
    } catch (err) {
      setDomainSaveError(err instanceof Error ? err.message : 'Failed to remove domain');
    }
  }

  async function runLookup(e: React.FormEvent) {
    e.preventDefault();
    const value = lookupValue.trim().toLowerCase();
    if (!value) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      // We pass the active customerId purely to satisfy the global
      // customer-scope guard for non-admin roles — the lookup itself
      // doesn't filter by customer (it queries third-party providers
      // with the value the analyst typed), but the guard requires every
      // /darkweb call to carry a customer the user has access to.
      const data = await apiFetch<EmailLookupResponse | DomainLookupResponse>(`/darkweb/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: lookupKind, value, customerId: activeCustomerId }),
      });
      setLookupResult(data);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-slate-800 dark:to-slate-900 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              External Exposure Center
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-4xl">
              Customer-level exposure view across dark-web mentions, infostealer logs, breach
              intelligence, certificate/code/external OSINT and analyst-triaged exposure findings.
              Use this page for triage and response; use the Phishing Domain Scanner for full
              lookalike-domain permutation investigation.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => openPhishingDomainScanner(monitoredDomains[0])}
              className="px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium"
            >
              Open Phishing Domain Scanner
            </button>
            <button
              type="button"
              onClick={loadScan}
              disabled={scanLoading}
              className="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-400 text-white text-sm font-medium"
            >
              {scanLoading ? 'Checking…' : 'Re-check Exposure'}
            </button>
          </div>
        </div>
        {scan?.generatedAt ? (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Last checked {fmtTs(scan.generatedAt)} for {scan.customerName}.
          </div>
        ) : null}
      </div>

      <ExposureOverviewCards scan={scan} monitoredDomains={monitoredDomains} />
      <SourceSplitCard />

      {/* Autonomous exposure briefing — runs in the background every 6h
          across OSINT sources. Surfaces AI-triaged findings with severity,
          summary, recommended action, and per-source health. */}
      <NeoBriefingCard />


      {/* Monitored Domains */}
      <section>
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Watched Domains for Exposure Monitoring
            <span className="ml-2 text-xs font-normal text-slate-500">
              ({monitoredDomains.length} on the list)
            </span>
          </h3>
        </header>

        {/* Admin-only: add new domain */}
        {isAdmin ? (
          <form onSubmit={addDomain} className="flex flex-wrap items-center gap-2 mb-4">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="acme.com"
              className="px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 min-w-[14rem]"
              disabled={domainSaving}
            />
            <button
              type="submit"
              disabled={domainSaving || !newDomain.trim()}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-sm font-medium"
            >
              {domainSaving ? 'Adding…' : 'Add domain'}
            </button>
            {domainSaveError ? (
              <span className="text-sm text-red-600 dark:text-red-400">{domainSaveError}</span>
            ) : null}
          </form>
        ) : null}

        {scanError ? (
          <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200 mb-3">
            {scanError}
          </div>
        ) : null}

        {monitoredDomains.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-8 text-center text-sm text-slate-500 dark:text-slate-400">
            This tenant has no domains on the watch list yet.
            {isAdmin
              ? ' Use the field above to add one.'
              : ' Ask an administrator to add one.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scan?.domains?.map((d) => (
              <DomainCard
                key={d.domain}
                result={d}
                onRemove={isAdmin ? () => removeDomain(d.domain) : undefined}
              />
            )) ?? (
              monitoredDomains.map((domain) => (
                <div
                  key={domain}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400"
                >
                  Loading {domain}…
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Quick Lookup */}
      <section>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">
          Ad-hoc Credential / Domain Lookup
        </h3>
        <form
          onSubmit={runLookup}
          className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3"
        >
          <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
            <button
              type="button"
              onClick={() => { setLookupKind('email'); setLookupResult(null); }}
              className={`px-3 py-1.5 text-sm font-medium ${
                lookupKind === 'email'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200'
              }`}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => { setLookupKind('domain'); setLookupResult(null); }}
              className={`px-3 py-1.5 text-sm font-medium border-l border-slate-300 dark:border-slate-600 ${
                lookupKind === 'domain'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200'
              }`}
            >
              Domain
            </button>
          </div>
          <input
            type={lookupKind === 'email' ? 'email' : 'text'}
            value={lookupValue}
            onChange={(e) => setLookupValue(e.target.value)}
            placeholder={lookupKind === 'email' ? 'analyst@acme.com' : 'acme.com'}
            className="flex-1 min-w-[16rem] px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100"
          />
          <button
            type="submit"
            disabled={lookupLoading || !lookupValue.trim()}
            className="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-400 text-white text-sm font-medium"
          >
            {lookupLoading ? 'Checking…' : 'Check'}
          </button>
        </form>

        {lookupError ? (
          <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
            {lookupError}
          </div>
        ) : null}

        {lookupResult?.kind === 'email' ? (
          <EmailLookupCard result={lookupResult} />
        ) : null}
        {lookupResult?.kind === 'domain' && lookupResult.domains[0] ? (
          <DomainCard result={lookupResult.domains[0]} />
        ) : null}

        {!lookupResult && !lookupError && !lookupLoading ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Email lookup searches infostealer logs. Domain lookup queries breach and infostealer exposure sources. Full typo-squatting permutation analysis lives in the Phishing Domain Scanner tab.
          </p>
        ) : null}
      </section>
    </div>
  );
}

// ── Per-domain card ─────────────────────────────────────────────────────
function DomainCard({
  result,
  onRemove,
}: {
  result: DomainScanResult;
  onRemove?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hibp = result.hibp.data;
  const hr = result.hudsonRock.data;
  const sev = exposureSeverity(hibp?.breachCount ?? 0, hr?.total ?? 0);

  return (
    <article className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <header className="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 break-all">
              {result.domain}
            </h4>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${SEV_PILL[sev]}`}>
              {SEV_LABEL[sev]}
            </span>
          </div>
        </div>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-slate-500 hover:text-red-600 dark:hover:text-red-400"
            title="Stop monitoring"
          >
            Remove
          </button>
        ) : null}
      </header>

      <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
        {/* HIBP column */}
        <div className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Breaches
          </div>
          {result.hibp.ok ? (
            <>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {fmtNum(hibp?.breachCount)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">known breaches</div>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                {fmtNum(hibp?.totalAccountsPwned)} accounts exposed in total
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Most recent breach: {fmtDate(hibp?.mostRecentBreachDate)}
              </div>
            </>
          ) : (
            <div className="text-sm text-amber-700 dark:text-amber-400">
              Couldn't reach Have I Been Pwned: {result.hibp.error ?? 'service unavailable'}
            </div>
          )}
        </div>
        {/* Hudson Rock column */}
        <div className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Infostealer infections
          </div>
          {result.hudsonRock.ok ? (
            <>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {fmtNum(hr?.total)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">infected machines</div>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Staff machines: {fmtNum(hr?.employees)} · Customer machines: {fmtNum(hr?.users)}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Outside services hit: {fmtNum(hr?.thirdParties)}
                {hr?.totalStealers ? (
                  <> · Total log entries: {fmtNum(hr.totalStealers)}</>
                ) : null}
              </div>
              {(hr?.lastEmployeeCompromised || hr?.lastUserCompromised) ? (
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Most recent infection - staff: {fmtDate(hr?.lastEmployeeCompromised)} · customer:{' '}
                  {fmtDate(hr?.lastUserCompromised)}
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-sm text-amber-700 dark:text-amber-400">
              Couldn't reach Hudson Rock: {result.hudsonRock.error ?? 'service unavailable'}
            </div>
          )}
        </div>
      </div>

      {/* Expand for breach detail (HIBP) */}
      {hibp && hibp.breaches.length > 0 ? (
        <details
          className="border-t border-slate-200 dark:border-slate-700 group"
          open={expanded}
          onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer px-4 py-2 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-700/40 select-none">
            {expanded ? 'Hide' : 'Show'} the {hibp.breaches.length} breach
            {hibp.breaches.length === 1 ? '' : 'es'} in detail
          </summary>
          <ol className="px-4 pb-4 space-y-3">
            {hibp.breaches.map((b) => (
              <li
                key={b.name}
                className="border-t border-slate-200 dark:border-slate-700 pt-3 first:border-t-0 first:pt-0"
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {b.title}
                    </span>
                    {breachFlagPills(b)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {fmtDate(b.breachDate)} · {fmtNum(b.pwnCount)} accounts
                  </div>
                </div>
                {b.description ? (
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed">
                    {stripHtml(b.description)}
                  </p>
                ) : null}
                {b.dataClasses.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {b.dataClasses.map((dc) => (
                      <span
                        key={dc}
                        className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      >
                        {dc}
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        </details>
      ) : null}

      {/* Hudson Rock — descriptive exposure detail */}
      {hr && hr.total > 0 ? <HudsonRockDetail hr={hr} /> : null}

      {/* Stealer families summary (always visible if present) */}
      {hr?.stealerFamilies && hr.stealerFamilies.length > 0 ? (
        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold">Top stealers:</span>{' '}
          {hr.stealerFamilies.slice(0, 5).map((s, i) => (
            <span key={s.name}>
              {i > 0 ? ', ' : ''}
              {s.name} <span className="text-slate-400">({fmtNum(s.count)})</span>
            </span>
          ))}
        </div>
      ) : null}
    </article>
  );
}

// ── HIBP breach helpers ─────────────────────────────────────────────────
function stripHtml(s: string): string {
  // HIBP descriptions contain anchor tags with linkbacks; strip tags and
  // collapse whitespace so they render cleanly as inline paragraph text.
  return s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}
function breachFlagPills(b: HibpBreach) {
  const pills: Array<{ label: string; cls: string }> = [];
  if (b.isMalware) pills.push({
    label: 'Malware',
    cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  });
  if (b.isSensitive) pills.push({
    label: 'Sensitive',
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  });
  if (b.isFabricated) pills.push({
    label: 'Fabricated',
    cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  });
  if (!b.isVerified) pills.push({
    label: 'Unverified',
    cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  });
  if (pills.length === 0) return null;
  return pills.map((p) => (
    <span
      key={p.label}
      className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${p.cls}`}
    >
      {p.label}
    </span>
  ));
}

// ── Hudson Rock descriptive exposure detail ─────────────────────────────
function HudsonRockDetail({ hr }: { hr: HudsonRockDomainResult }) {
  // Defensive: every array may be missing on stale/legacy responses (e.g. a
  // page that has cached results from before the parser was upgraded).
  const empUrls = hr.topEmployeeUrls ?? [];
  const cliUrls = hr.topClientUrls ?? [];
  const apps = hr.topApplications ?? [];
  const thirdParty = hr.topThirdPartyDomains ?? [];
  const hasUrls = empUrls.length > 0 || cliUrls.length > 0;
  const hasApps = apps.length > 0;
  const has3rdParty = thirdParty.length > 0;
  const hasPasswords = !!hr.employeePasswords || !!hr.userPasswords;
  const hasAv = !!hr.antivirusCoverage;
  if (!hasUrls && !hasApps && !has3rdParty && !hasPasswords && !hasAv) return null;
  return (
    <details className="border-t border-slate-200 dark:border-slate-700">
      <summary className="cursor-pointer px-4 py-2 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-700/40 select-none">
        Show what the infostealers captured
      </summary>
      <div className="px-4 pb-4 pt-1 space-y-3">
        {empUrls.length > 0 ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Sites where staff credentials were stolen from
            </div>
            <ul className="space-y-0.5">
              {empUrls.slice(0, 8).map((u) => (
                <li key={u.url} className="text-xs text-slate-600 dark:text-slate-300 flex gap-2">
                  <span className="text-slate-400 dark:text-slate-500 tabular-nums w-10 text-right">
                    {fmtNum(u.occurrence)}
                  </span>
                  <span className="font-mono break-all">{u.url}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {cliUrls.length > 0 ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Sites where customer credentials were stolen from
            </div>
            <ul className="space-y-0.5">
              {cliUrls.slice(0, 8).map((u) => (
                <li key={u.url} className="text-xs text-slate-600 dark:text-slate-300 flex gap-2">
                  <span className="text-slate-400 dark:text-slate-500 tabular-nums w-10 text-right">
                    {fmtNum(u.occurrence)}
                  </span>
                  <span className="font-mono break-all">{u.url}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {hasUrls ? (
          <p className="text-[10px] italic text-slate-500 dark:text-slate-400 -mt-1">
            URLs with a <code>*</code> in them are partly hidden on the free plan. The full URLs
            and the matching email addresses are only visible to paid Hudson Rock subscribers.
          </p>
        ) : null}

        {hasApps ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Apps that show up most in the stealer logs
            </div>
            <div className="flex flex-wrap gap-1">
              {apps.map((a) => (
                <span
                  key={a.name}
                  className="px-1.5 py-0.5 rounded text-[11px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                >
                  {a.name} <span className="text-slate-400">×{fmtNum(a.count)}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {has3rdParty ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Other services your team also signs into
            </div>
            <div className="flex flex-wrap gap-1">
              {thirdParty.map((d) => (
                <span
                  key={d.domain}
                  className="px-1.5 py-0.5 rounded text-[11px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-mono"
                >
                  {d.domain} <span className="text-slate-400">×{fmtNum(d.occurrence)}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {hasPasswords ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {hr.employeePasswords ? (
              <PasswordStrengthBar
                title={`Staff password strength (sample size ${fmtNum(hr.employeePasswords.totalPass)})`}
                p={hr.employeePasswords}
              />
            ) : null}
            {hr.userPasswords ? (
              <PasswordStrengthBar
                title={`Customer password strength (sample size ${fmtNum(hr.userPasswords.totalPass)})`}
                p={hr.userPasswords}
              />
            ) : null}
          </div>
        ) : null}

        {hasAv && hr.antivirusCoverage ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Antivirus on the infected machines (sample size {fmtNum(hr.antivirusCoverage.total)})
            </div>
            <div className="flex h-2 rounded overflow-hidden bg-slate-200 dark:bg-slate-700 mb-1">
              <div
                className="bg-emerald-500"
                style={{ width: `${hr.antivirusCoverage.withAvPercent}%` }}
                title={`Had antivirus: ${hr.antivirusCoverage.withAvPercent}%`}
              />
              <div
                className="bg-rose-500"
                style={{ width: `${hr.antivirusCoverage.withoutAvPercent}%` }}
                title={`No antivirus: ${hr.antivirusCoverage.withoutAvPercent}%`}
              />
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Had antivirus running: {hr.antivirusCoverage.withAvPercent}% · No antivirus:{' '}
              {hr.antivirusCoverage.withoutAvPercent}%
              {hr.antivirusCoverage.topProducts.length > 0 ? (
                <>
                  {' · '}most common products:{' '}
                  {hr.antivirusCoverage.topProducts
                    .slice(0, 5)
                    .map((p) => `${p.name} (${fmtNum(p.count)})`)
                    .join(', ')}
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        <p className="text-[11px] italic text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-200 dark:border-slate-700">
          Neither service shows you the actual email addresses behind these numbers on their
          free plans. The figures above are everything we can see without paying for a
          subscription on either side.
        </p>
      </div>
    </details>
  );
}

function PasswordStrengthBar({ title, p }: { title: string; p: PasswordStrength }) {
  const colorFor = (label: string): string => {
    switch (label) {
      case 'Too weak': return 'bg-rose-500';
      case 'Weak': return 'bg-amber-500';
      case 'Medium': return 'bg-yellow-400';
      case 'Strong': return 'bg-emerald-500';
      default: return 'bg-slate-400';
    }
  };
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
        {title}
      </div>
      <div className="flex h-2 rounded overflow-hidden bg-slate-200 dark:bg-slate-700 mb-1">
        {p.buckets.map((b) => (
          <div
            key={b.label}
            className={colorFor(b.label)}
            style={{ width: `${b.perc}%` }}
            title={`${b.label}: ${b.qty} (${b.perc}%)`}
          />
        ))}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-2">
        {p.buckets.map((b) => (
          <span key={b.label}>
            {b.label}: {b.perc}%
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Email lookup result card ────────────────────────────────────────────
function EmailLookupCard({ result }: { result: EmailLookupResponse }) {
  const data = result.hudsonRock.data;
  return (
    <article className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <header className="flex items-center justify-between mb-3">
        <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 break-all">
          {result.value}
        </h4>
        {result.hudsonRock.ok ? (
          data?.found ? (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
              EXPOSED
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
              CLEAN
            </span>
          )
        ) : (
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            LOOKUP FAILED
          </span>
        )}
      </header>

      {!result.hudsonRock.ok ? (
        <div className="text-sm text-amber-700 dark:text-amber-400">
          Hudson Rock didn't respond: {result.hudsonRock.error ?? 'service unavailable'}
        </div>
      ) : !data?.found ? (
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {data?.message ?? 'Hudson Rock has no record of this email turning up in any infostealer log.'}
        </div>
      ) : (
        <>
          <div className="text-sm text-slate-600 dark:text-slate-300 mb-1">
            This email shows up in <span className="font-semibold">{fmtNum(data.total)}</span>{' '}
            infostealer log{data.total === 1 ? '' : 's'}.
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Work accounts exposed: {fmtNum(data.totalCorporateServices)} ·
            personal accounts exposed: {fmtNum(data.totalUserServices)}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                <tr>
                  <th className="py-1 pr-2">Stealer family</th>
                  <th className="py-1 pr-2">Date stolen</th>
                  <th className="py-1 pr-2">Machine</th>
                  <th className="py-1 pr-2">OS</th>
                  <th className="py-1">Country</th>
                </tr>
              </thead>
              <tbody>
                {data.infections.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-200 dark:border-slate-700">
                    <td className="py-1.5 pr-2 text-slate-900 dark:text-slate-100">{it.stealerFamily ?? ' - '}</td>
                    <td className="py-1.5 pr-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">{fmtDate(it.date)}</td>
                    <td className="py-1.5 pr-2 text-slate-600 dark:text-slate-300">{it.computerName ?? ' - '}</td>
                    <td className="py-1.5 pr-2 text-slate-600 dark:text-slate-300">{it.operatingSystem ?? ' - '}</td>
                    <td className="py-1.5 text-slate-600 dark:text-slate-300">{it.country ?? ' - '}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </article>
  );
}
